import {
    ChangeEvent,
    FC,
    KeyboardEvent,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { noOp } from '../../lib/utils';
import { ValidatedStringMessage } from '../ValidatedStringMessage/ValidatedStringMessage';

export type InputProps = {
  /**
   * Value of the input
   */
  initialValue?: string | number | null;
  /**
   * Placeholder of the input
   */
  placeholder?: string;
  /**
   * Flag to disable input
   */
  disabled?: boolean;
  /**
   * Flag for error
   */
  error?: boolean;
  /**
   * Type of input
   */
  type?: 'text' | 'number' | 'password' | 'email';
  /**
   * Maximum length for input
   */
  maxLength?: number;
  /**
   * Maximum number value
   */
  maxNumber?: number;
  /**
   * Minimum number value
   */
  minNumber?: number;
  /**
   * In/decrementation steps for number values
   */
  step?: number;
  /**
   * Variant of input
   */
  variant?: 'default' | 'transparent' | 'dropdown-filter';
  /**
   * Font weight for input
   */
  fontWeight?: string;
  /**
   * Autofocus flag
   */
  autofocus?: boolean;
  /**
   * Autosuggest setting
   */
  autoComplete?: string;
  /**
   * Reduced padding
   */
  compact?: boolean;
  /**
   * Change action of the input
   */
  onChange?(event: ChangeEvent<HTMLInputElement>): void;
  /**
   * Focus action of the input
   */
  onFocus?(): void;
  /**
   * Blur action of the input
   */
  onBlur?(event: ChangeEvent<HTMLInputElement>): void;
  /**
   * On key down action of the input
   */
  onKeyDown?(event: KeyboardEvent<HTMLInputElement>): void;
  /**
   * Get validation result for the Input
   */
  onValidate?(isValid: boolean): void;
};

const BASE_INPUT_CLASSES = 'outline-none w-full font-regular';
const DEFAULT_INPUT_CLASSES = `${BASE_INPUT_CLASSES} placeholder-gray-600 text-gray-700 hover:border-blue-100 focus:border-blue-100 font-regular`;
const DISABLED_INPUT_CLASSES = `${BASE_INPUT_CLASSES} border-gray-200 placeholder-gray-400 text-gray-400 focus:outline-none`;
const ERROR_INPUT_CLASSES = `${BASE_INPUT_CLASSES} border-red-200 placeholder-gray-600 text-gray-700 hover:border-blue-100 focus:border-blue-100`;

export const Input: FC<InputProps> = ({
  initialValue,
  placeholder,
  disabled,
  error,
  type,
  maxLength = Infinity,
  maxNumber,
  minNumber,
  step,
  variant,
  autofocus,
  autoComplete,
  compact,
  fontWeight = 'font-regular',
  onChange = noOp,
  onFocus,
  onBlur,
  onKeyDown,
  onValidate = noOp,
}: InputProps) => {
  // make `value` a string, no matter of the type of `initialValue`
  const [value, setValue] = useState(`${initialValue ?? ''}`);
  const [isValid, setIsValid] = useState(true);

  const className = useMemo(
    () =>
      [
        ...(function* () {
          if (disabled) {
            yield DISABLED_INPUT_CLASSES;
          } else if (error || !isValid) {
            yield ERROR_INPUT_CLASSES;
          } else {
            yield DEFAULT_INPUT_CLASSES;
          }

          switch (variant) {
            case 'transparent':
              yield 'border-gray-700 bg-transparent border-b';
              break;
            case 'dropdown-filter':
              yield 'bg-white border-gray-500 border-b';
              yield compact ? 'p-1' : 'p-2';
              break;
            case 'default':
            default:
              yield 'border-gray-500 bg-white border rounded';
              yield compact ? 'p-1' : 'p-2';
              break;
          }
          yield fontWeight;
        })(),
      ].join(' '),
    [disabled, error, isValid, variant, fontWeight, compact],
  );

  useEffect(() => {
    setValue(`${initialValue ?? ''}`);
  }, [initialValue, setValue]);

  return (
    <>
      <input
        data-testid='input'
        value={value}
        className={`w-inherit ${className}`}
        placeholder={placeholder}
        disabled={disabled}
        type={type}
        step={step ?? 'any'}
        max={maxNumber}
        min={minNumber}
        onKeyDown={onKeyDown}
        autoFocus={autofocus}
        autoComplete={autoComplete}
        onChange={(event: ChangeEvent<HTMLInputElement>) => {
          setValue(event.target.value);
          onChange(event);
        }}
        onFocus={onFocus}
        onBlur={onBlur}
      />
      <ValidatedStringMessage
        maxLength={maxLength}
        valueLength={value.length}
        onValidate={(valid) => {
          setIsValid(valid);
          onValidate(valid);
        }}
      />
    </>
  );
};
